import { useState } from 'react';
import { httpsCallable } from '../../lib/callable';
import { Copy, Check, Loader2, X, AlertTriangle } from 'lucide-react';
import { functions } from '../../firebase';
import { CLOUD_FUNCTIONS, CALLABLE_TIMEOUTS } from '../../constants/firebase';
import { useToast } from '../../contexts/ToastContext';
import {
  AccessRole,
  ACCESS_ROLE_DESCRIPTIONS,
  ACCESS_ROLE_LABELS,
} from '../../lib/permissions';

interface InviteMemberModalProps {
  projectId: string;
  onClose: () => void;
}

const INVITABLE_ROLES: AccessRole[] = ['manager', 'editor', 'reviewer', 'viewer'];

interface InviteResult {
  acceptUrl: string;
  invitedEmail: string;
  emailQueued: boolean;
}

export default function InviteMemberModal({
  projectId,
  onClose,
}: InviteMemberModalProps) {
  const { showToast } = useToast();
  const [email, setEmail] = useState('');
  const [accessRole, setAccessRole] = useState<AccessRole>('editor');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<InviteResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const fn = httpsCallable(functions, CLOUD_FUNCTIONS.INVITE_MEMBER, { timeout: CALLABLE_TIMEOUTS.SHORT });
      const baseUrl = `${window.location.protocol}//${window.location.host}`;
      const res = await fn({ projectId, email, accessRole, baseUrl });
      const data = res.data as InviteResult;
      const fullUrl = data.acceptUrl.startsWith('http')
        ? data.acceptUrl
        : `${baseUrl}${data.acceptUrl}`;
      setResult({
        acceptUrl: fullUrl,
        invitedEmail: data.invitedEmail,
        emailQueued: data.emailQueued,
      });
    } catch (err: unknown) {
      const msg =
        err instanceof Error ? err.message : 'Failed to send invitation.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopy = async () => {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(result.acceptUrl);
      setCopied(true);
      showToast('Invite link copied', 'success');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      showToast('Could not copy. Select and copy manually.', 'error');
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">
            {result ? 'Invitation ready' : 'Invite a team member'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {result ? (
          <div className="p-6 space-y-4">
            <p className="text-sm text-gray-700">
              An invitation has been recorded for{' '}
              <span className="font-medium">{result.invitedEmail}</span>.
            </p>
            {!result.emailQueued && (
              <div className="rounded-lg bg-amber-50 ring-1 ring-amber-200 p-3 flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                <p className="text-sm text-amber-900">
                  Email delivery isn't configured for this project, so the
                  invitee won't receive an email automatically. Copy the
                  link below and share it with them directly.
                </p>
              </div>
            )}
            {result.emailQueued && (
              <div className="rounded-lg bg-emerald-50 ring-1 ring-emerald-200 p-3 text-sm text-emerald-900">
                The invitation email has been queued for delivery. You can
                also share the link below as a backup.
              </div>
            )}
            <div>
              <label className="block text-xs font-medium uppercase tracking-wide text-gray-500 mb-1">
                Invite link
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  readOnly
                  value={result.acceptUrl}
                  onFocus={e => e.currentTarget.select()}
                  className="flex-1 rounded-lg ring-1 ring-gray-200 px-3 py-2 text-sm font-mono text-gray-700"
                />
                <button
                  type="button"
                  onClick={handleCopy}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 transition-colors"
                >
                  {copied ? (
                    <Check className="h-4 w-4" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                  {copied ? 'Copied' : 'Copy'}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                The recipient will need to sign in with this email address
                to accept. The link expires in 14 days.
              </p>
            </div>
            <div className="flex justify-end pt-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-sm font-medium text-gray-700 transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <div>
              <label
                htmlFor="invite-email"
                className="block text-xs font-medium uppercase tracking-wide text-gray-500 mb-1"
              >
                Email address
              </label>
              <input
                id="invite-email"
                type="email"
                required
                autoFocus
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="colleague@example.com"
                className="w-full rounded-lg ring-1 ring-gray-200 focus:ring-primary-500 focus:ring-2 px-3 py-2 text-sm outline-none"
              />
            </div>
            <div>
              <label className="block text-xs font-medium uppercase tracking-wide text-gray-500 mb-2">
                Role
              </label>
              <div className="space-y-2">
                {INVITABLE_ROLES.map(role => (
                  <label
                    key={role}
                    className={`flex items-start gap-3 px-3 py-2 rounded-lg cursor-pointer ring-1 ring-inset transition-colors ${
                      accessRole === role
                        ? 'ring-primary-500 bg-primary-50'
                        : 'ring-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="radio"
                      name="accessRole"
                      value={role}
                      checked={accessRole === role}
                      onChange={() => setAccessRole(role)}
                      className="mt-1"
                    />
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {ACCESS_ROLE_LABELS[role]}
                      </p>
                      <p className="text-xs text-gray-500">
                        {ACCESS_ROLE_DESCRIPTIONS[role]}
                      </p>
                    </div>
                  </label>
                ))}
              </div>
            </div>
            {error && (
              <div className="rounded-lg bg-red-50 ring-1 ring-red-200 p-3 text-sm text-red-800">
                {error}
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-sm font-medium text-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting || !email}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                Send invite
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
